<template>
  <div>
    <div class="page-toolbar">
      <div>
        <div class="page-title">地区管理</div>
        <div class="page-description">维护一级地区、二级地区、历史别名、排序和启用状态；停用不会改写历史商品。</div>
      </div>
      <div class="toolbar-actions">
        <el-button icon="el-icon-refresh" @click="loadCatalog">刷新</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openGroupDialog()">新增一级地区</el-button>
      </div>
    </div>

    <el-row :gutter="18">
      <el-col :xs="24" :lg="10">
        <el-card class="page-card" shadow="never">
          <div slot="header" class="card-header">
            <span>一级地区</span>
            <span class="muted">{{ groups.length }} 项</span>
          </div>
          <el-table :data="groups" v-loading="loading" highlight-current-row @current-change="selectGroup">
            <el-table-column prop="name" label="名称" min-width="140" />
            <el-table-column prop="sortOrder" label="排序" width="72" />
            <el-table-column label="状态" width="76">
              <template slot-scope="{ row }">
                <el-tag :type="row.enabled ? 'success' : 'info'" size="mini">{{ row.enabled ? '启用' : '停用' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="updatedAt" label="最近保存" width="150" />
            <el-table-column label="操作" width="70" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" @click.stop="openGroupDialog(row)">编辑</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <el-col :xs="24" :lg="14">
        <el-card class="page-card" shadow="never">
          <div slot="header" class="card-header">
            <div>
              <span>{{ activeGroup ? `${activeGroup.name}的二级地区` : '二级地区' }}</span>
              <span v-if="activeGroup" class="muted">最近保存：{{ activeGroup.updatedAt || '-' }}</span>
            </div>
            <el-button type="primary" size="mini" icon="el-icon-plus" :disabled="!activeGroup" @click="openItemDialog()">新增二级地区</el-button>
          </div>
          <el-empty v-if="!activeGroup && !loading" description="请先选择一级地区" :image-size="72" />
          <el-table v-else :data="activeItems" v-loading="loading">
            <el-table-column prop="name" label="名称" min-width="150" />
            <el-table-column prop="aliases" label="历史别名" min-width="150">
              <template slot-scope="{ row }">{{ (row.aliases || []).join('、') || '-' }}</template>
            </el-table-column>
            <el-table-column prop="sortOrder" label="排序" width="72" />
            <el-table-column label="状态" width="76">
              <template slot-scope="{ row }">
                <el-tag :type="row.enabled ? 'success' : 'info'" size="mini">{{ row.enabled ? '启用' : '停用' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="默认" width="76">
              <template slot-scope="{ row }">
                <el-tag v-if="row.defaultSelected" type="warning" size="mini">默认</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="updatedAt" label="最近保存" width="150" />
            <el-table-column label="操作" width="135" fixed="right">
              <template slot-scope="{ row }">
                <el-button v-if="!row.defaultSelected" type="text" size="mini" :disabled="!row.enabled || !activeGroup.enabled" @click="setDefault(row)">设默认</el-button>
                <el-button type="text" size="mini" @click="openItemDialog(row)">编辑</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px" @closed="resetForm">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="98px">
        <el-form-item label="名称" prop="name">
          <el-input v-model.trim="form.name" maxlength="80" show-word-limit />
        </el-form-item>
        <el-form-item label="历史别名">
          <el-input v-model="form.aliasesText" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="每行一个；改名后的旧名称会自动保留" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sortOrder" :min="0" :max="999999" controls-position="right" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.enabled" active-text="启用" inactive-text="停用" />
        </el-form-item>
        <el-alert type="info" :closable="false" show-icon title="为保留历史商品匹配关系，地区只能停用，不提供物理删除。" />
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  createRegionGroup,
  createRegionItem,
  fetchRegions,
  setDefaultRegionItem,
  updateRegionGroup,
  updateRegionItem
} from '../../api/admin'
import { isAuthRedirectError } from '../../utils/auth'

export default {
  name: 'RegionListView',
  data() {
    return {
      loading: false,
      saving: false,
      catalog: { groups: [] },
      selectedGroupId: '',
      dialogVisible: false,
      dialogType: 'group',
      editingId: '',
      form: this.createEmptyForm(),
      rules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    groups() {
      return Array.isArray(this.catalog.groups) ? this.catalog.groups : []
    },
    activeGroup() {
      return this.groups.find(item => item.id === this.selectedGroupId) || null
    },
    activeItems() {
      return this.activeGroup && Array.isArray(this.activeGroup.items) ? this.activeGroup.items : []
    },
    dialogTitle() {
      const subject = this.dialogType === 'group' ? '一级地区' : '二级地区'
      return `${this.editingId ? '编辑' : '新增'}${subject}`
    }
  },
  created() {
    this.loadCatalog()
  },
  methods: {
    createEmptyForm() {
      return { name: '', aliasesText: '', sortOrder: null, enabled: true }
    },
    async loadCatalog() {
      const previousId = this.selectedGroupId
      this.loading = true
      try {
        const catalog = await fetchRegions()
        this.catalog = catalog && typeof catalog === 'object' ? catalog : { groups: [] }
        const matched = this.groups.find(item => item.id === previousId)
        this.selectedGroupId = matched ? matched.id : (this.groups[0] ? this.groups[0].id : '')
      } catch (error) {
        if (!isAuthRedirectError(error)) this.$message.error(error.message || '地区目录获取失败')
      } finally {
        this.loading = false
      }
    },
    selectGroup(row) {
      this.selectedGroupId = row && row.id ? row.id : ''
    },
    openGroupDialog(row = null) {
      this.dialogType = 'group'
      this.editingId = row && row.id ? row.id : ''
      this.form = row ? {
        name: row.name || '',
        aliasesText: (row.aliases || []).join('\n'),
        sortOrder: row.sortOrder,
        enabled: row.enabled !== false
      } : this.createEmptyForm()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    openItemDialog(row = null) {
      if (!this.activeGroup) return
      this.dialogType = 'item'
      this.editingId = row && row.id ? row.id : ''
      this.form = row ? {
        name: row.name || '',
        aliasesText: (row.aliases || []).join('\n'),
        sortOrder: row.sortOrder,
        enabled: row.enabled !== false
      } : this.createEmptyForm()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    resetForm() {
      this.form = this.createEmptyForm()
      this.editingId = ''
      if (this.$refs.formRef) this.$refs.formRef.clearValidate()
    },
    buildPayload() {
      return {
        name: this.form.name,
        aliases: Array.from(new Set(String(this.form.aliasesText || '').split(/\r?\n/).map(item => item.trim()).filter(Boolean))),
        sortOrder: this.form.sortOrder === null || this.form.sortOrder === undefined ? null : Number(this.form.sortOrder),
        enabled: this.form.enabled !== false
      }
    },
    submitForm() {
      if (!this.$refs.formRef) return
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        this.saving = true
        try {
          const payload = this.buildPayload()
          if (this.dialogType === 'group') {
            if (this.editingId) await updateRegionGroup(this.editingId, payload)
            else await createRegionGroup(payload)
          } else if (this.editingId) {
            await updateRegionItem(this.editingId, payload)
          } else {
            await createRegionItem(this.activeGroup.id, payload)
          }
          this.$message.success('保存成功')
          this.dialogVisible = false
          await this.loadCatalog()
        } catch (error) {
          if (!isAuthRedirectError(error)) this.$message.error(error.message || '地区保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    async setDefault(row) {
      try {
        await this.$confirm(`确认将“${row.name}”设为小程序默认地区吗？`, '提示', { type: 'warning' })
        await setDefaultRegionItem(row.id)
        this.$message.success('默认地区已更新')
        await this.loadCatalog()
      } catch (error) {
        if (error && error !== 'cancel' && error !== 'close' && !isAuthRedirectError(error)) {
          this.$message.error(error.message || '设置默认地区失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.toolbar-actions,
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.page-description,
.muted {
  margin-top: 5px;
  color: #8792aa;
  font-size: 12px;
}

.muted {
  margin-left: 8px;
}
</style>
